/**
 * TV Mission Control Display API Contract
 *
 * Pins the request/response shape for the unattended office-wall display
 * (`GET /internal/display/mission-control`), authorized by the revocable
 * display token rather than a user session. One call returns everything the
 * wall renders: the live-ops map, the online-users panel, and the
 * infrastructure panel.
 *
 * The Kotlin client mirrors these field names BY HAND — there is no codegen
 * across that boundary. Keep them stable and boring; a rename here is a silent
 * client break that only shows up as a blank panel on a wall nobody is
 * watching at the time.
 *
 * ONE AUTHORITATIVE POPULATION (R15). `map.dots` is the source of truth for who
 * is online, and `onlineUsers` is a projection OF those same dots — not a second
 * count from a second query. The two available sources count differently (the
 * map restricts to an eight-event client allowlist; the named rollup counts any
 * event), so deriving the panel total independently would put a number on the
 * wall that visibly disagrees with the dots beside it. The rollup contributes
 * display NAMES only.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-07-22-004-feat-tv-mission-control-dashboard-plan.md (U3).
 */

import { z } from 'zod'
import { SchoolTierSchema } from '../../schemas/entities/school-segmentation'
import { MapPersonDotSchema, MapPersonRoleSchema, MapStatsSchema } from './map-data'

/**
 * A school pin on the wall's basemap.
 *
 * `logoUrl` is the school's dark-variant logo (the basemap is dark), falling
 * back to the default logo, then null. `activeUserCount` is the number of active
 * app-user dots that resolved to this school in the current window — 0 for a
 * school with nobody online (every plotted school appears regardless).
 *
 * `type` is `elementary | middle | high | null` — the three buckets the pin
 * colors understand, or null when unknown. `isCustomer` is NOT carried: every
 * pin here is an initialized customer school by construction.
 *
 * `tier` is the school's subscription tier — the shared `SchoolTier`, reused
 * verbatim rather than a display-local enum, so the wall and every other tier
 * consumer speak the same five lifecycle values plus `unknown`. The wall renders
 * `paying` prominently and everything else recessively, so `unknown` (no
 * resolvable primary subscription) FAILS SAFE into the recessive bucket — a
 * school is never promoted to prominent because we could not classify it. The
 * field is REQUIRED: the backend always supplies a value.
 */
export const DisplaySchoolPinSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['elementary', 'middle', 'high']).nullable(),
  lat: z.number(),
  lng: z.number(),
  logoUrl: z.string().nullable(),
  activeUserCount: z.number(),
  tier: SchoolTierSchema
})

export type DisplaySchoolPin = z.infer<typeof DisplaySchoolPinSchema>

/**
 * The map payload. `dots` reuses the web map's dot shape (they are the same
 * data), but `schools` uses the display-owned `DisplaySchoolPinSchema` so the
 * wall can carry per-school logos the web map does not.
 *
 * `lastUpdated` is NOT repeated here — the composed response carries a single
 * top-level stamp for the whole poll.
 */
export const DisplayMapSchema = z.object({
  dots: z.array(MapPersonDotSchema),
  schools: z.array(DisplaySchoolPinSchema),
  stats: MapStatsSchema
})

export type DisplayMap = z.infer<typeof DisplayMapSchema>

/**
 * One named person in the online-users panel.
 *
 * `personId` is the same id as the corresponding `map.dots[].personId`, so the
 * client can correlate a panel row with the dot it belongs to. `role` is the
 * authoritative DB-resolved role carried by that dot — all six values, not the
 * three legend buckets: a staff / admin / uncategorized person renders in the
 * neutral ink rather than being dropped from the wall (R2).
 *
 * `name` NEVER contains an email address. A person with no resolvable name is
 * the literal `Unknown`; see `services/reporting/active-users-by-school`.
 */
export const DisplayActiveUserSchema = z.object({
  personId: z.string(),
  name: z.string(),
  role: MapPersonRoleSchema
})

export type DisplayActiveUser = z.infer<typeof DisplayActiveUserSchema>

/**
 * A school with at least one person online. Schools with nobody online are
 * omitted entirely — this is the panel's breakdown, not a fleet roster (the
 * full pin set lives in `map.schools`).
 *
 * `activeUserCount` always equals `users.length`; it is carried explicitly so
 * the client can render the count without walking the list.
 */
export const DisplayActiveSchoolSchema = z.object({
  id: z.string(),
  name: z.string(),
  activeUserCount: z.number(),
  users: z.array(DisplayActiveUserSchema)
})

export type DisplayActiveSchool = z.infer<typeof DisplayActiveSchoolSchema>

/**
 * The online-users panel.
 *
 * `total` is the authoritative head count and equals `map.stats.activeUserCount`
 * — it is that number, not a recount (R15).
 *
 * `unaffiliatedCount` is the people in `total` who resolved to no school and so
 * appear under no `schools` row. It exists so the panel's arithmetic closes:
 * `total === sum(schools[].activeUserCount) + unaffiliatedCount`. Without it a
 * viewer sees rows that quietly fail to add up to the headline and has no way
 * to tell a bug from a person we could not place.
 *
 * NO CAPS. Every school with anyone online appears, and every online person
 * appears under their school. The desktop widget's top-5 cap is deliberately
 * not inherited: on a wall, a silently dropped school is worse than a long list.
 */
export const DisplayOnlineUsersSchema = z.object({
  total: z.number(),
  unaffiliatedCount: z.number(),
  schools: z.array(DisplayActiveSchoolSchema)
})

export type DisplayOnlineUsers = z.infer<typeof DisplayOnlineUsersSchema>

/**
 * SQS depths for the infrastructure panel (R3).
 *
 * `totalMessages` is the three named queues summed. `totalDlq` is summed across
 * EVERY monitored queue's dead-letter queue, not read off one of them — a
 * single-queue read reports 0 while messages pile up next door.
 */
export const DisplayQueuesSchema = z.object({
  highPriority: z.number(),
  lowPriority: z.number(),
  streamSync: z.number(),
  totalMessages: z.number(),
  totalDlq: z.number(),
  overallHealth: z.enum(['healthy', 'warning', 'error'])
})

export type DisplayQueues = z.infer<typeof DisplayQueuesSchema>

/**
 * ECS sync-worker liveness for the infrastructure panel (R3).
 */
export const DisplayWorkersSchema = z.object({
  running: z.number(),
  desired: z.number(),
  pending: z.number(),
  status: z.enum(['scaling', 'stable'])
})

export type DisplayWorkers = z.infer<typeof DisplayWorkersSchema>

export const DisplayInfrastructureSchema = z.object({
  queues: DisplayQueuesSchema,
  workers: DisplayWorkersSchema
})

export type DisplayInfrastructure = z.infer<typeof DisplayInfrastructureSchema>

/**
 * The revenue strip (R5) — ARR, paying accounts, and renewals inside 30 days.
 *
 * `countedAccounts` and `knownPayers` carry R4's coverage onto the wall, and
 * they are not optional decoration. This is the ONE screen nobody can drill
 * into, so it is the one that most needs the caveat on it: an unqualified ARR
 * figure on a wall reads as complete while silently omitting the payers we could
 * not link. `countedAccounts` is what the ARR was summed from; `knownPayers` is
 * how many we know about. When they differ, the client must render the
 * qualifier, not the bare number.
 *
 * `available: false` with zeroed figures means the HubSpot read failed — NOT
 * that revenue is zero. A wall showing "$0 ARR" reads as a business collapse
 * rather than a missing field, so a client seeing `available: false` renders no
 * strip at all rather than zeros.
 *
 * Money is a plain number in whole US dollars, matching `revenue.ts`.
 */
export const DisplayRevenueSchema = z.object({
  arr: z.number().nonnegative(),
  payingAccounts: z.number().int().nonnegative(),
  renewalsNext30: z.number().int().nonnegative(),
  countedAccounts: z.number().int().nonnegative(),
  knownPayers: z.number().int().nonnegative(),
  available: z.boolean()
})

export type DisplayRevenue = z.infer<typeof DisplayRevenueSchema>

/**
 * GET /internal/display/mission-control — query params.
 * `minutesBack` is the active-window size; defaults to 15. The SAME window is
 * applied to every source so the panels describe one moment.
 */
export const DisplayMissionControlRequestSchema = z.object({
  minutesBack: z.coerce.number().int().positive().max(1440).optional().default(15)
})

export type DisplayMissionControlRequest = z.infer<typeof DisplayMissionControlRequestSchema>

/**
 * GET /internal/display/mission-control — response.
 *
 * Every section degrades independently: a dead dependency yields that section's
 * empty/zero value with HTTP 200 rather than failing the whole poll. The client
 * cannot currently distinguish "degraded" from "genuinely zero" — a per-source
 * status discriminator is an explicitly deferred Phase 2 item, recorded in the
 * plan's Scope Boundaries.
 */
export const DisplayMissionControlResponseSchema = z.object({
  map: DisplayMapSchema,
  onlineUsers: DisplayOnlineUsersSchema,
  infrastructure: DisplayInfrastructureSchema,
  /**
   * ADDITIVE-ONLY, AND THAT IS THE WHOLE POINT (KTD7). A TV stick in the office
   * runs whatever build is installed on it and updates on its own schedule, so
   * this key is `.optional()` in BOTH directions: a payload from a backend that
   * predates revenue still parses here, and a client that predates revenue must
   * tolerate the key appearing. Making it required would turn a wall upgrade into
   * a blank screen nobody is watching at the time.
   */
  revenue: DisplayRevenueSchema.optional(),
  lastUpdated: z.string()
})

export type DisplayMissionControlResponse = z.infer<typeof DisplayMissionControlResponseSchema>
